/**
 * Pure display-ordering util for QA #21 — a job's `location` field is a single
 * free-text string (packages/schema/src/jobs.ts, JobRecord.location in
 * api.ts), sometimes carrying MULTIPLE locations separated by `;` (e.g. a
 * remote-friendly listing: "London, UK; Ontario, CAN; Remote-Friendly, United
 * States; San Francisco, CA"). Left as-is, a US-based viewer has to read past
 * every other geography before finding the one that's actually relevant to
 * them.
 *
 * This is PRESENTATION ONLY — it reorders segments of an already-rendered
 * string for a US viewer, it never re-scores/re-buckets a job and never
 * writes anything back. It deliberately does NOT duplicate the backend's
 * inferTargetCountry engine (apps/api/lib/eligibility.ts) — that function
 * does real ISO-3166 country inference (structured geo IDs first, then a
 * work-authorization prefix, then a text regex) to feed ELIGIBILITY/SCORING
 * decisions, which is a business rule that belongs server-side, not
 * duplicated here as a second, divergent copy (no-frontend-bandaids). Per the
 * brief's own accepted fallback ("accept a simple heuristic: US entries first
 * for en-US users, preserving the rest"), this is a small, self-contained
 * display heuristic: split on `;`, float any segment that plainly reads as
 * United States to the front, keep everything else in its original relative
 * order. A single-segment (no `;`) location is returned unchanged — there is
 * nothing to reorder.
 *
 * Segments not confidently detected as US are left exactly where they were —
 * this never asserts a country for a segment it isn't sure about.
 */

/** US state/territory postal abbreviations — a segment ending in ", XX" with
 *  one of these codes is unambiguously a US location (mirrors the same
 *  "trust the concrete signal over a guess" spirit as inferTargetCountry's
 *  own structured-geo-first precedence). */
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
]);

/** Explicit "United States" phrasing, in any of its common listing forms
 *  (United States, U.S., U.S, US, USA). The tail uses a negative lookahead
 *  rather than `\b` — `\b` cannot anchor immediately after a literal `.` at
 *  the end of a string (no trailing word character to form a boundary
 *  against), which silently failed to match "U.S." with a plain `\bu\.s\.\b`
 *  pattern. Verified against no-false-positive country names that merely
 *  CONTAIN "us" as a substring (Australia, Russia, Belarus, Mauritius) — the
 *  leading `\bu\.?s\.?a?` requires the token to itself start with a U. */
const US_COUNTRY_PHRASE = /\bunited states\b|\bu\.?s\.?a?\.?(?!\w)/i;

/** A segment ending in a comma + two-letter US state code, e.g.
 *  "San Francisco, CA" or "Remote-Friendly, United States, TX". */
function endsWithUsStateCode(segment: string): boolean {
  const match = segment.trim().match(/,\s*([A-Za-z]{2})\s*$/);
  if (!match) return false;
  return US_STATE_CODES.has(match[1].toUpperCase());
}

/** True when a single `;`-delimited segment plainly reads as a US location —
 *  either explicit "United States"/"U.S."/"USA" phrasing, or a trailing US
 *  state postal code. Never guesses from a bare city name alone (a city like
 *  "Ontario, CAN" must NOT match — Ontario is also a Canadian province, which
 *  is exactly why this checks the trailing token, not a citylist). */
export function looksLikeUsSegment(segment: string): boolean {
  const trimmed = segment.trim();
  if (!trimmed) return false;
  if (US_COUNTRY_PHRASE.test(trimmed)) return true;
  return endsWithUsStateCode(trimmed);
}

/**
 * Reorders a `;`-delimited multi-location string so US-looking segments lead,
 * for a US-based (en-US) viewer. Non-US segments and the relative order among
 * each group are preserved exactly. A location with no `;` (a single
 * segment), or null/empty input, is returned unchanged.
 */
export function orderLocationForUS(location: string | null | undefined): string | null {
  if (location == null) return null;
  if (!location.includes(';')) return location;

  const segments = location.split(';').map((s) => s.trim()).filter((s) => s.length > 0);
  if (segments.length <= 1) return location;

  const us: string[] = [];
  const rest: string[] = [];
  for (const segment of segments) {
    (looksLikeUsSegment(segment) ? us : rest).push(segment);
  }
  return [...us, ...rest].join('; ');
}
